import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShopping } from '../contexts/ShoppingContext';
import { useCheckout } from '../contexts/CheckoutContext';
import { useUser } from '../contexts/UserContext';
import { CheckoutFormData, AccountingCode, ShippingRate, ShippingLocation, SpecialDeliveryFormData } from '../types';
import { PromoCodeSection } from './checkout/PromoCodeSection';
import { AccountingCodeSection } from './checkout/AccountingCodeSection';
import { ShippingAddressSection } from './checkout/ShippingAddressSection';
import { ShippingMethodSection } from './checkout/ShippingMethodSection';
import { SpecialDeliverySection } from './checkout/SpecialDeliverySection';
import { PaymentMethodSection } from './checkout/PaymentMethodSection';
import { CreditCardSection } from './checkout/CreditCardSection';
import { OrderSummary } from './checkout/OrderSummary';
import { GuestEmailSection } from './checkout/GuestEmailSection';
import { useGuestMode } from '../hooks/useGuestMode';
import { useClientData } from '../hooks/useClientData';
import { ShippingService } from '../services/shippingService';
import { OrderService } from '../services/orderService';
import { ApiError } from '../errors/ApiError';

export function Checkout() {
  const navigate = useNavigate();
  const { state } = useShopping();
  const { checkoutData, loading: checkoutLoading, error: checkoutError } = useCheckout();
  const { user } = useUser();
  const { isGuest } = useGuestMode();
  const { clientData } = useClientData();
  const [sdiErrors, setSdiErrors] = useState<Partial<Record<keyof SpecialDeliveryFormData, string>>>({});
  const [formData, setFormData] = useState<CheckoutFormData>({
    useNewAddress: false,
    billingAddress: {
      sameAsShipping: true,
      streetline1: '',
      streetline2: '',
      city: '',
      state: '',
      postalcode: '',
      phone: '',
    },
    creditCard: {
      cardholderFirstName: '',
      cardholderLastName: '',
    },
  });
  const [dynamicShippingRates, setDynamicShippingRates] = useState<ShippingRate[] | null>(null);
  const [shippingRatesLoading, setShippingRatesLoading] = useState(false);
  const [shippingRatesError, setShippingRatesError] = useState<string | null>(null);
  const [addressValidationWarnings, setAddressValidationWarnings] = useState<string[]>([]);
  const [pendingAddressRequest, setPendingAddressRequest] = useState<{
    address: ShippingLocation | Partial<ShippingLocation>;
    isNewAddress: boolean;
  } | null>(null);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // Set useNewAddress to true for guest users once guest mode is determined
  useEffect(() => {
    if (isGuest) {
      setFormData((prev) => ({ ...prev, useNewAddress: true }));
    }
  }, [isGuest]);

  // Fetch shipping rates for a given address
  const fetchShippingRates = useCallback(async (
    address: ShippingLocation | Partial<ShippingLocation>,
    isNewAddress: boolean,
    skipValidation = false
  ) => {
    // Convert address to request format
    const request = isNewAddress
      ? ShippingService.newAddressToRequest(address)
      : ShippingService.locationToRequest(address as ShippingLocation);

    // If new address is incomplete, don't fetch yet
    if (!request) {
      return;
    }

    setShippingRatesLoading(true);
    setShippingRatesError(null);
    setAddressValidationWarnings([]);
    setPendingAddressRequest(null);

    try {
      const result = await ShippingService.getShippingRates(request, skipValidation);

      // Check if address validation failed
      if (!result.addressValidated && result.validationWarnings.length > 0) {
        setAddressValidationWarnings(result.validationWarnings);
        setPendingAddressRequest({ address, isNewAddress });
        setDynamicShippingRates(null);
        return;
      }

      setDynamicShippingRates(result.rates);

      // Auto-select the first rate if none selected
      if (result.rates.length > 0 && !formData.shippingRate) {
        setFormData((prev) => ({ ...prev, shippingRate: result.rates[0] }));
      }

      // If no rates returned, show an appropriate error
      if (result.rates.length === 0) {
        setShippingRatesError('No shipping options available. Please ensure your cart has items.');
      }
    } catch {
      setShippingRatesError('Unable to load shipping rates. Please check your address.');
      setDynamicShippingRates(null);
    } finally {
      setShippingRatesLoading(false);
    }
  }, [formData.shippingRate]);

  // Continue with unvalidated address
  const handleContinueWithUnvalidatedAddress = useCallback(() => {
    if (pendingAddressRequest) {
      fetchShippingRates(
        pendingAddressRequest.address,
        pendingAddressRequest.isNewAddress,
        true // skip validation
      );
    }
  }, [pendingAddressRequest, fetchShippingRates]);

  // Handle saved address selection
  const handleSelectSavedAddress = useCallback((location: ShippingLocation) => {
    setFormData((prev) => ({ ...prev, shippingLocation: location, shippingRate: undefined }));
    fetchShippingRates(location, false);
  }, [fetchShippingRates]);

  // Track whether we've already auto-fetched rates for the current new address
  const hasAutoFetchedRates = useRef(false);

  // Handle new address field changes — auto-fetch once when zip reaches 5+ chars
  const handleNewAddressChange = useCallback((address: Partial<ShippingLocation>) => {
    setFormData((prev) => ({ ...prev, newAddress: address }));

    if (
      !hasAutoFetchedRates.current &&
      address.address1 && address.city && address.state &&
      address.zip && address.zip.length >= 5
    ) {
      hasAutoFetchedRates.current = true;
      fetchShippingRates(address, true);
    }
  }, [fetchShippingRates]);

  // Manual re-fetch for the button shown after initial auto-fetch
  const handleFetchRates = useCallback(() => {
    if (formData.newAddress) {
      fetchShippingRates(formData.newAddress, true);
    }
  }, [formData.newAddress, fetchShippingRates]);

  // Use totals from checkout data, with selected shipping rate overriding the initial value
  const subtotal = checkoutData?.netTotals.subtotal || 0;
  const shippingCost = formData.shippingRate?.chargePrice ?? checkoutData?.netTotals.shipping ?? 0;
  const tax = checkoutData?.netTotals.tax || 0;
  const total = subtotal + shippingCost + tax;
  const promoDiscount = checkoutData?.discounts.subtotal || 0;
  const configurationWarnings = checkoutData?.configurationWarnings ?? [];

  // Destructure data from checkoutData
  const promoCodes = checkoutData?.promoCodes ? Object.values(checkoutData.promoCodes) : [];
  const savedShippingLocations = checkoutData?.savedShippingLocations || [];
  // Use dynamic shipping rates if available, otherwise fall back to checkout data
  const shippingRates = dynamicShippingRates ?? (checkoutData?.shippingRates ? Object.values(checkoutData.shippingRates) : []);
  const paymentTypes = checkoutData?.paymentTypes || [];

  // Get accounting codes from user's client, converting ClientAccountingCode to AccountingCode format
  const accountingCodes: AccountingCode[] = user?.client?.accounting_codes
    ? user.client.accounting_codes.map((code) => ({
        id: code.id,
        name: code.name,
        active: code.status,
      }))
    : [];

  // Get the single accounting code value from user for display-only mode
  const userAccountingCode = user?.accountingCode ?? undefined;

  // Determine if a valid shipping address has been provided
  const hasValidAddress = formData.useNewAddress
    ? !!(formData.newAddress?.address1 && formData.newAddress?.city && formData.newAddress?.state && formData.newAddress?.zip)
    : !!formData.shippingLocation;

  // Set default shipping location and recipient info on load
  useEffect(() => {
    if (checkoutData?.shippingLocation) {
      setFormData((prev) => ({ ...prev, shippingLocation: checkoutData.shippingLocation }));
    }
    if (checkoutData?.shippingRate) {
      setFormData((prev) => ({ ...prev, shippingRate: checkoutData.shippingRate }));
    }
    if (checkoutData?.shippingName) {
      setFormData((prev) => ({ ...prev, recipientName: prev.recipientName ?? checkoutData.shippingName ?? undefined }));
    }
    if (checkoutData?.shippingPhone) {
      setFormData((prev) => ({ ...prev, recipientPhone: prev.recipientPhone ?? checkoutData.shippingPhone ?? undefined }));
    }
  }, [checkoutData]);

  useEffect(() => {
    if (pendingSubmit && formData.paymentToken) {
      setPendingSubmit(false);
      setSubmissionError(null);
      submitOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submitOrder is intentionally excluded; it captures current formData at call time and including it would cause re-fires
  }, [formData.paymentToken, pendingSubmit]);

  const handleSubmit = async () => {
    setSubmissionError(null);

    // Validate required fields
    if (!formData.paymentType) {
      setSubmissionError('Please select a payment method');
      return;
    }

    // For credit card payments, ensure we have a token
    if (formData.paymentType === 'CREDIT') {
      if (!formData.paymentToken) {
        // Validate credit card form fields before tokenizing
        const ccForm = document.getElementById('credit-card-form') as HTMLFormElement | null;
        if (ccForm && !ccForm.reportValidity()) {
          return;
        }

        // Trigger tokenization first
        const tokenizeCard = (window as unknown as Record<string, unknown>).tokenizeCreditCard as (() => void) | undefined;
        if (tokenizeCard) {
          tokenizeCard();
          setPendingSubmit(true);
          setSubmissionError('Processing payment information...');
          setTimeout(() => {
            setPendingSubmit((pending) => {
              if (pending) {
                setSubmissionError('Payment processing timed out. Please try again.');
              }
              return false;
            });
          }, 30000);
          return;
        }
        setSubmissionError('Please enter your credit card information');
        return;
      }
    }

    // For guests, require email
    if (isGuest && !formData.guestEmail) {
      setSubmissionError('Please enter your email address');
      return;
    }

    // Validate Special Delivery Instructions when enabled
    if (clientData?.specialDeliveryInstructions && formData.shippingRate) {
      const sdi = formData.specialDelivery;
      const newSdiErrors: Partial<Record<keyof SpecialDeliveryFormData, string>> = {};
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (!sdi?.deliveryStartDate) {
        newSdiErrors.deliveryStartDate = 'Delivery start date is required';
      } else if (new Date(sdi.deliveryStartDate) < today) {
        newSdiErrors.deliveryStartDate = 'Delivery start date cannot be in the past';
      } else if (clientData.specialDeliveryMinDayToEvent != null) {
        const msPerDay = 1000 * 60 * 60 * 24;
        const daysUntilDelivery = Math.round(
          (new Date(sdi.deliveryStartDate).getTime() - today.getTime()) / msPerDay
        );
        if (daysUntilDelivery > clientData.specialDeliveryMinDayToEvent) {
          newSdiErrors.deliveryStartDate = `Orders must be placed within ${clientData.specialDeliveryMinDayToEvent} days of delivery start date`;
        }
      }

      if (!sdi?.deliveryEndDate) {
        newSdiErrors.deliveryEndDate = 'Delivery end date is required';
      } else if (sdi.deliveryStartDate && new Date(sdi.deliveryEndDate) < new Date(sdi.deliveryStartDate)) {
        newSdiErrors.deliveryEndDate = 'Delivery end date cannot be before delivery start date';
      }

      if (sdi?.eventStartDate || sdi?.eventEndDate) {
        if (sdi.eventStartDate && sdi.deliveryEndDate && new Date(sdi.eventStartDate) < new Date(sdi.deliveryEndDate)) {
          newSdiErrors.eventStartDate = 'Event start date must be on or after delivery end date';
        }
        if (sdi.eventEndDate && sdi.eventStartDate && new Date(sdi.eventEndDate) < new Date(sdi.eventStartDate)) {
          newSdiErrors.eventEndDate = 'Event end date cannot be before event start date';
        }
      }

      if (Object.keys(newSdiErrors).length > 0) {
        setSdiErrors(newSdiErrors);
        return;
      }
    }

    setSdiErrors({});
    await submitOrder();
  };

  const submitOrder = async () => {
    setIsSubmitting(true);

    try {
      const result = await OrderService.submitOrder({
        guestEmail: formData.guestEmail,
        recipientName: formData.recipientName,
        recipientPhone: formData.recipientPhone,
        savePhone: formData.savePhone,
        paymentType: formData.paymentType!,
        paymentToken: formData.paymentToken,
        cardholderFirstName: formData.creditCard?.cardholderFirstName,
        cardholderLastName: formData.creditCard?.cardholderLastName,
        billingAddress: formData.billingAddress,
        shippingAddress: formData.useNewAddress ? {
          address1: formData.newAddress?.address1 || '',
          address2: formData.newAddress?.address2 || undefined,
          city: formData.newAddress?.city || '',
          state: formData.newAddress?.state || '',
          postalcode: formData.newAddress?.zip || '',
          countrycode: formData.newAddress?.countrycode || 'US',
          residential: true,
        } : undefined,
        comments: formData.comments,
        shippingLocationId: formData.useNewAddress ? undefined : formData.shippingLocation?.id,
        shippingServiceType: formData.shippingRate?.serviceType,
        shippingChargePrice: formData.shippingRate?.chargePrice,
        specialDeliveryInstructions: formData.specialDelivery,
      });

      // Navigate to order confirmation page
      navigate(`/order-confirmation/${result.masterOrderNo}`);
    } catch (error) {
      console.error('Order submission failed:', error);
      if (error instanceof ApiError) {
        setSubmissionError(error.message);
      } else if (error instanceof Error) {
        setSubmissionError(error.message);
      } else {
        setSubmissionError('Failed to submit order. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (state.cart.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Your cart is empty</h2>
          <button
            onClick={() => navigate('/')}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while checkout data is being fetched
  if (checkoutLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading checkout data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if checkout data failed to load
  if (checkoutError || !checkoutData) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Failed to load checkout data</h2>
          <p className="text-gray-600 mb-4">{checkoutError}</p>
          <button
            onClick={() => window.location.reload()}
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Continue Shopping
          </button>
          <h1 className="text-3xl font-bold text-gray-900">Checkout</h1>
        </div>

        {/* Configuration warnings - products with missing dimensions */}
        {configurationWarnings.length > 0 && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-red-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <div>
                <p className="font-semibold text-red-800 mb-1">Your order cannot be submitted:</p>
                {configurationWarnings.map((warning, index) => (
                  <p key={index} className="text-red-700">{warning}</p>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Guest Email Section - Only for guests */}
            {isGuest && (
              <GuestEmailSection
                email={formData.guestEmail}
                onEmailChange={(guestEmail) =>
                  setFormData({ ...formData, guestEmail })
                }
              />
            )}

            {/* Promo Code and Accounting Code - Only for logged-in users */}
            {!isGuest && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <PromoCodeSection
                  promoCodes={promoCodes}
                  selectedPromoCode={formData.promoCode}
                  onPromoCodeChange={(promoCode) =>
                    setFormData({ ...formData, promoCode })
                  }
                />
                <AccountingCodeSection
                  accountingCodes={accountingCodes}
                  selectedAccountingCode={formData.accountingCode}
                  onAccountingCodeChange={(accountingCode) =>
                    setFormData({ ...formData, accountingCode })
                  }
                  displayOnlyValue={userAccountingCode}
                />
              </div>
            )}

            <ShippingAddressSection
              savedShippingLocations={savedShippingLocations}
              useNewAddress={formData.useNewAddress}
              selectedShippingLocation={formData.shippingLocation}
              newAddress={formData.newAddress}
              onToggleAddressMode={(useNew) => {
                setFormData({
                  ...formData,
                  useNewAddress: useNew,
                  shippingLocation: useNew ? undefined : formData.shippingLocation,
                  shippingRate: undefined,
                });
                // Clear dynamic rates, validation warnings, and reset auto-fetch flag when switching modes
                setDynamicShippingRates(null);
                setAddressValidationWarnings([]);
                setPendingAddressRequest(null);
                hasAutoFetchedRates.current = false;
              }}
              onSelectSavedAddress={handleSelectSavedAddress}
              onNewAddressChange={handleNewAddressChange}
              onFetchRates={handleFetchRates}
              shippingRatesLoading={shippingRatesLoading}
              showFetchRatesButton={hasAutoFetchedRates.current}
              isGuest={isGuest}
              recipientName={formData.recipientName}
              recipientPhone={formData.recipientPhone}
              onRecipientNameChange={(recipientName) =>
                setFormData({ ...formData, recipientName })
              }
              onRecipientPhoneChange={(recipientPhone) =>
                setFormData({ ...formData, recipientPhone })
              }
              savePhone={formData.savePhone}
              onSavePhoneChange={(savePhone) =>
                setFormData({ ...formData, savePhone })
              }
              internationalShipping={!!clientData?.internationalShipping}
            />

            {/* Address validation warning - shown when address could not be validated */}
            {addressValidationWarnings.length > 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Address Validation</h2>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <div className="flex-1">
                      {addressValidationWarnings.map((warning, index) => (
                        <p key={index} className="text-yellow-800">{warning}</p>
                      ))}
                      <button
                        type="button"
                        onClick={handleContinueWithUnvalidatedAddress}
                        className="mt-3 px-4 py-2 bg-yellow-600 text-white font-medium rounded-lg hover:bg-yellow-700 transition-colors"
                      >
                        Continue with Unvalidated Address
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping rates loading indicator - only shown after address is provided and no validation warnings */}
            {hasValidAddress && shippingRatesLoading && addressValidationWarnings.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Method</h2>
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-600">Loading shipping rates...</span>
                </div>
              </div>
            )}

            {/* Shipping rates error - only shown after address is provided and no validation warnings */}
            {hasValidAddress && !shippingRatesLoading && shippingRatesError && addressValidationWarnings.length === 0 && (
              <div className="bg-white rounded-xl shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Shipping Method</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-red-800">{shippingRatesError}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Shipping method selection - only shown when address is provided, validated, and rates are available */}
            {hasValidAddress && !shippingRatesLoading && !shippingRatesError && addressValidationWarnings.length === 0 && shippingRates.length > 0 && (
              <ShippingMethodSection
                shippingRates={shippingRates}
                selectedRate={formData.shippingRate}
                onRateChange={(rate) => setFormData({ ...formData, shippingRate: rate })}
              />
            )}

            {clientData?.specialDeliveryInstructions && !!formData.shippingRate && (
              <SpecialDeliverySection
                value={formData.specialDelivery ?? {
                  deliveryStartDate: '',
                  deliveryEndDate: '',
                  eventStartDate: '',
                  eventEndDate: '',
                  shipmentContent: '',
                }}
                onChange={(specialDelivery) => setFormData({ ...formData, specialDelivery })}
                minDayToEvent={clientData.specialDeliveryMinDayToEvent}
                errors={sdiErrors}
              />
            )}

            {formData.shippingRate && (
              <PaymentMethodSection
                paymentTypes={paymentTypes}
                selectedPaymentType={formData.paymentType}
                onPaymentTypeChange={(paymentType) =>
                  setFormData({ ...formData, paymentType })
                }
              />
            )}

            {/* Credit Card Section - shown when CREDIT payment type is selected */}
            {formData.paymentType === 'CREDIT' && (
              <>
                <CreditCardSection
                  billingAddress={formData.billingAddress!}
                  creditCard={formData.creditCard!}
                  shippingAddress={formData.useNewAddress ? formData.newAddress : formData.shippingLocation}
                  onBillingAddressChange={(billingAddress) =>
                    setFormData({ ...formData, billingAddress })
                  }
                  onCreditCardChange={(creditCard) =>
                    setFormData({ ...formData, creditCard })
                  }
                  onTokenReceived={(paymentToken) => {
                    setFormData((prev) => ({ ...prev, paymentToken }));
                    setPaymentError(null);
                  }}
                  onError={(err) => {
                    setPaymentError(err);
                    setPendingSubmit(false);
                    setSubmissionError(null);
                  }}
                  totalAmount={total}
                />
              </>
            )}
          </div>

          <div className="lg:col-span-1 self-start">
            <OrderSummary
              cartItems={state.cart}
              subtotal={subtotal}
              promoDiscount={promoDiscount}
              shippingCost={shippingCost}
              tax={tax}
              total={total}
              canSubmit={!!formData.paymentType && !isSubmitting && configurationWarnings.length === 0}
              isSubmitting={isSubmitting}
              paymentError={paymentError}
              submissionError={submissionError}
              onSubmit={handleSubmit}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
